"use client"
import { Offer, OffersOffer } from '@/services/dataTypes'
import Image from 'next/image'
import React, { useEffect, useState } from 'react'
import { calculateOfferDuration, discardHTMLTags, getBaseImageUrl, getCurrencySymbol, getFinalDiscountTag, getMerchantHref, getProductDetailHref } from '@/constants/hooks'
import OfferOutUrl from '@/components/shared/OfferOutUrl'
import OfferModalTrigger from './OfferModalTrigger'
import { Clock } from 'lucide-react'
import Link from 'next/link'

interface Props {
    product: OffersOffer,
    companyId: string,
    awaited_p_id?: string,
    mer_slug_type: string,
    mer_slug: string,
    domain: string,
    ads_campaign: boolean,
    onOfferClick: (offer: OffersOffer, merchantHref: string) => void,
}

// Deterministic theme per offer
const THEMES = [
    { label: 'POPULAR', accent: 'text-indigo-600', accentBg: 'bg-indigo-50', accentBorder: 'border-indigo-100', btn: 'bg-slate-900 hover:bg-indigo-600' },
    { label: 'EXCLUSIVE', accent: 'text-indigo-600', accentBg: 'bg-indigo-50', accentBorder: 'border-indigo-100', btn: 'bg-slate-900 hover:bg-indigo-600' },
    { label: 'BEST OFFER', accent: 'text-indigo-600', accentBg: 'bg-indigo-50', accentBorder: 'border-indigo-100', btn: 'bg-slate-900 hover:bg-indigo-600' },
    { label: 'LIMITED', accent: 'text-indigo-600', accentBg: 'bg-indigo-50', accentBorder: 'border-indigo-100', btn: 'bg-slate-900 hover:bg-indigo-600' },
]

function getThemeByIndex(id: string | number) {
    const seed = String(id).split('').reduce((a, c) => a + c.charCodeAt(0), 0)
    return THEMES[seed % THEMES.length]
}

function parseDiscountDisplay(tag: string | null): { prefix: string; value: string; suffix: string } {
    if (tag) {
        const m = tag.match(/^(.*?)(\d+(?:\.\d+)?%)\s*(.*?)$/i)
        if (m) return { prefix: m[1].trim() || '', value: m[2], suffix: m[3].trim() || 'OFF' }
        return { prefix: '', value: tag, suffix: '' }
    }
    return { prefix: '', value: '', suffix: '' }
}

function getPeopleUsed(id: string | number): number {
    const seed = String(id).split('').reduce((a, c) => a + c.charCodeAt(0), 0)
    return 50 + (seed % 300)
}

const OffersListView = ({ product, companyId, awaited_p_id, mer_slug_type, mer_slug, domain, onOfferClick }: Props) => {
    const merchantHref = getMerchantHref(product.merchant, mer_slug, mer_slug_type)

    const offer = product?.offer
    const imageSrc = (offer?.offer_type?.name === 'product' && offer?.product_image
        ? getBaseImageUrl(domain, offer.product_image, '')
        : getBaseImageUrl(domain, product?.merchant?.merchant_logo, '')) || '';

    const [imageError, setImageError] = useState(!imageSrc)

    useEffect(() => {
        setImageError(!imageSrc)
    }, [imageSrc])

    const productDetailUrl = offer?.slug
        ? getProductDetailHref(product.merchant, mer_slug_type, offer.slug, offer.category?.slug)
        : null

    const originalPrice = offer?.original_price ? parseFloat(offer.original_price) : 0
    const salePrice = offer?.sale_price ? parseFloat(offer.sale_price) : 0
    const discountPercent = originalPrice > 0 && salePrice > 0
        ? Math.round(((originalPrice - salePrice) / originalPrice) * 100) : null
    const finalDiscountTag = getFinalDiscountTag(offer?.offer_title || offer?.offer_detail, discountPercent)
    const { prefix, value, suffix } = parseDiscountDisplay(finalDiscountTag)
    const duration = calculateOfferDuration(offer?.end_date)
    const theme = getThemeByIndex(offer?.unique_id || offer?.id || 0)
    const peopleUsed = getPeopleUsed(offer?.unique_id || offer?.id || 0)

    const title = (() => {
        const t = discardHTMLTags(offer?.offer_title?.replaceAll('_', ' ') || '')
        if (t.length <= 70) return t
        const cut = t.slice(0, 70)
        return cut.slice(0, cut.lastIndexOf(' ') || 70) + '...'
    })()

    const isProduct = offer?.offer_type?.name === 'product'
    const hasCoupon = !!offer?.coupon_code
    const isLifetime = !duration || duration?.toLowerCase().includes('lifetime') || duration?.toLowerCase().includes('life time') || duration?.toLowerCase().includes('no expiry')

    return (
        <div className="group relative bg-white rounded-[2rem] p-6 border border-slate-100 hover:border-indigo-100 shadow-[0_4px_20px_rgba(0,0,0,0.03)] hover:shadow-[0_20px_40px_rgba(79,70,229,0.12)] hover:-translate-y-1.5 transition-all duration-500 flex flex-col justify-between h-full overflow-hidden select-none">

            {/* Subtle gradient hover bg */}
            <div className="absolute inset-0 bg-gradient-to-br from-indigo-50/0 to-indigo-50/0 group-hover:from-indigo-50/50 group-hover:to-white transition-colors duration-500 -z-10" />

            {/* Top Section */}
            <div className="flex-1 flex flex-col">
                {/* Header Row: Floating badges */}
                <div className="flex items-center justify-between gap-1.5 mb-4 w-full min-w-0 flex-wrap sm:flex-nowrap">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] sm:text-[11px] font-bold ${theme.accentBg} ${theme.accent} border ${theme.accentBorder} shrink-0 max-w-[80%] truncate`}>
                        {theme.label}
                    </span>
                    {duration && !isLifetime ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] sm:text-[11px] font-bold tracking-widest text-amber-600 bg-amber-50/80 border border-amber-100/50 shrink-0 whitespace-nowrap ml-auto">
                            <Clock className="w-2.5 h-2.5" />
                            {duration}
                        </span>
                    ) : (
                        <span className="inline-flex items-center gap-0.5 text-[10px] sm:text-[11px] uppercase font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-100/50 shrink-0 whitespace-nowrap ml-auto">
                            Verified
                        </span>
                    )}
                </div>

                {/* Logo container */}
                <div className="relative w-32 h-32 bg-white rounded-3xl p-3.5 shadow-sm border border-slate-50 group-hover:scale-105 transition-transform duration-500 flex items-center justify-center mx-auto mb-4 z-10">
                    {!imageError ? (
                        <Image
                            src={imageSrc}
                            alt={product?.merchant?.merchant_name || ''}
                            width={400}
                            height={400}
                            quality={100}
                            className="object-contain w-full h-full p-2"
                            onError={() => setImageError(true)}
                        />
                    ) : (
                        <div className={`w-10 h-10 rounded-full ${theme.accentBg} ${theme.accent} flex items-center justify-center text-sm font-black`}>
                            {(product?.merchant?.merchant_name || 'D')[0]}
                        </div>
                    )}
                </div>

                {/* Discount Value */}
                {value ? (
                    <div className="text-center mb-2">
                        {prefix && <span className="block text-[8px] font-bold text-slate-400 tracking-wider mb-0.5">{prefix}</span>}
                        <div className="inline-flex items-baseline gap-1">
                            <span className={`text-2xl font-black ${theme.accent} leading-none tracking-tight`}>{value}</span>
                            {suffix && <span className="text-xs font-black text-slate-700 leading-none">{suffix}</span>}
                        </div>
                    </div>
                ) : null}

                {/* Price/Product details */}
                {isProduct && (offer?.sale_price || offer?.original_price) && (
                    <div className="flex items-baseline justify-center gap-1.5 mb-3 leading-none">
                        {offer?.sale_price && (
                            <span className={`text-lg font-black ${theme.accent}`}>
                                {getCurrencySymbol(offer?.currency)}{offer.sale_price}
                            </span>
                        )}
                        {offer?.original_price && (
                            <span className="text-xs line-through text-slate-400 font-medium">
                                {getCurrencySymbol(offer?.currency)}{offer.original_price}
                            </span>
                        )}
                    </div>
                )}

                {/* Title Description Container with URLs matching button logic */}
                <div className="flex-grow flex flex-col justify-center my-2 min-h-[2.5rem]">
                    {offer?.is_detail === 1 && productDetailUrl ? (
                        <Link href={productDetailUrl}>
                            <p className="text-[15px] font-bold text-slate-800 hover:text-indigo-600 transition-colors duration-300 text-center leading-snug line-clamp-2">
                                {title}
                            </p>
                        </Link>
                    ) : (
                        <OfferOutUrl
                            unique_id={offer?.unique_id}
                            outUrl={offer?.url}
                            merchantHref={merchantHref}
                            domain={domain}
                            customClass="block"
                        >
                            <p className="text-[15px] font-bold text-slate-800 hover:text-indigo-600 transition-colors duration-300 text-center leading-snug line-clamp-2">
                                {title}
                            </p>
                        </OfferOutUrl>
                    )}
                </div>

                {/* View Details — opens ProductDetailsModal */}
                <div className="flex justify-end mb-3 mt-2">
                    <OfferModalTrigger
                        product={offer}
                        merchantName={product?.merchant?.merchant_name || ''}
                        merchantLogo={getBaseImageUrl(domain, product?.merchant?.merchant_logo, '')}
                        domain={domain}
                    >
                        <span className="text-[11px] font-bold text-slate-900 hover:text-indigo-600 transition-colors tracking-wider cursor-pointer">
                            View Details
                        </span>
                    </OfferModalTrigger>
                </div>

                {/* Spacer Line */}
                <div className="w-full border-t border-dashed border-slate-200/80 mt-auto pt-2"></div>
            </div>

            {/* Bottom Section */}
            <div className="mt-auto">
                {/* Stats row */}
                <div className="mb-3 text-[9px] font-bold text-slate-400">
                    <span>{peopleUsed} Used Today</span>
                </div>

                {/* CTA Action - standard button redirect */}
                {offer?.is_detail === 1 && productDetailUrl ? (
                    <Link
                        href={productDetailUrl}
                        className={`w-full py-3 flex items-center justify-center text-white text-[11px] font-bold tracking-wider rounded-xl transition-all duration-300 shadow-md hover:shadow-indigo-500/20 active:scale-95 ${theme.btn}`}
                    >
                        {isProduct ? 'Buy Now' : 'Grab Deal'}
                    </Link>
                ) : (
                    <OfferOutUrl
                        unique_id={offer?.unique_id}
                        outUrl={offer?.url}
                        merchantHref={merchantHref}
                        domain={domain}
                        customClass={`w-full py-3 flex items-center justify-center text-white text-[11px] font-bold tracking-wider rounded-xl transition-all duration-300 shadow-md hover:shadow-indigo-500/20 active:scale-95 ${theme.btn}`}
                    >
                        {isProduct ? 'Buy Now' : 'Grab Deal'}
                    </OfferOutUrl>
                )}
            </div>
        </div>
    );
}

export default OffersListView